import { getControllerType, getDeviceOwnership } from './controllerTypes';

const safeField = (val) => (val !== undefined && val !== null ? val : null);

export function parseThresholdRange(value) {
  if (value === undefined || value === null || value === '') return null;
  if (Array.isArray(value)) {
    const nums = value.map((item) => Number(item)).filter((item) => Number.isFinite(item));
    return nums.length ? nums : null;
  }
  const nums = String(value)
    .split(/\s*-\s*|,|;/)
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item));
  return nums.length ? nums : null;
}

export function normalizeThresholds(thresholds = {}, fallback = {}) {
  const next = {
    day: {
      temperature:
        parseThresholdRange(thresholds?.day?.temperature ?? thresholds?.tempDay) ??
        parseThresholdRange(fallback?.day?.temperature),
      humidity:
        parseThresholdRange(thresholds?.day?.humidity ?? thresholds?.humDay) ??
        parseThresholdRange(fallback?.day?.humidity),
      co2:
        parseThresholdRange(thresholds?.day?.co2 ?? thresholds?.co2Day) ??
        parseThresholdRange(fallback?.day?.co2),
    },
    night: {
      temperature:
        parseThresholdRange(thresholds?.night?.temperature ?? thresholds?.tempNight) ??
        parseThresholdRange(fallback?.night?.temperature),
      humidity:
        parseThresholdRange(thresholds?.night?.humidity ?? thresholds?.humNight) ??
        parseThresholdRange(fallback?.night?.humidity),
      co2:
        parseThresholdRange(thresholds?.night?.co2 ?? thresholds?.co2Night) ??
        parseThresholdRange(fallback?.night?.co2),
    },
  };
  return next;
}

/**
 * @param {object} d — элемент из GET /api/devices/active (или такой же формы с комнаты)
 * @param {(deviceLike: object) => boolean} checkOnlineStatus — как в DevicesPage
 */
export function mapApiActiveDeviceToCardDevice(d, checkOnlineStatus) {
  const hasTelemetryContract = Object.prototype.hasOwnProperty.call(d || {}, 'latestTelemetry');
  const telemetry = d.latestTelemetry && typeof d.latestTelemetry === 'object' ? d.latestTelemetry : null;
  const sensorSource = telemetry || (hasTelemetryContract ? {} : d);
  const deviceStatus = d.deviceStatus && typeof d.deviceStatus === 'object' ? d.deviceStatus : {};
  const telemetryTimestamp =
    d.lastRealSensorUpdateAt ||
    d.latestTelemetryAt ||
    d.lastTelemetryAt ||
    telemetry?.timestamp ||
    (hasTelemetryContract ? null : d.timestamp);
  const telemetryTimestampMs = telemetryTimestamp ? new Date(telemetryTimestamp).getTime() : 0;
  const sensorDataFreshness =
    d.sensorDataFreshness && typeof d.sensorDataFreshness === 'object'
      ? d.sensorDataFreshness
      : {
          fresh:
            Number.isFinite(telemetryTimestampMs) &&
            telemetryTimestampMs > 0 &&
            Date.now() - telemetryTimestampMs <= 5 * 60 * 1000,
          lastSampleAt: telemetryTimestamp || null,
        };
  const resolvedStatuses = sensorSource.deviceStatuses || d.deviceStatuses || d.meta?.device_statuses || {};
  const controlOutlets = Array.isArray(d?.control?.outlets) ? d.control.outlets : [];
  const outletStateByKey = Object.fromEntries(controlOutlets.map((outlet) => [outlet?.key, outlet?.state]));
  const toRelayBit = (value) => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'boolean') return value ? 1 : 0;
    const token = String(value).trim().toLowerCase();
    if (['on', 'active', 'true'].includes(token)) return 1;
    if (['off', 'inactive', 'false'].includes(token)) return 0;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? (numeric === 1 ? 1 : 0) : undefined;
  };
  const relayAc = toRelayBit(outletStateByKey.ac);
  const relayDryer = toRelayBit(outletStateByKey.dryer);
  const relayCo2 = toRelayBit(outletStateByKey.co2);
  const relayExtra = toRelayBit(outletStateByKey.extra);
  const normalizedThresholds = normalizeThresholds(d.thresholds || {});
  const toDisplay = (s) => (s ? s.replace(/_/g, ' ') : s);
  const mqttEnabled = d.mqttEnabled === true || d.meta?.mqttEnabled === true;
  const controllerType = getControllerType(d);
  return {
    id: d.device_id,
    deviceId: d.deviceId || d.device_id,
    name: toDisplay(d.customName || d.name || d.device_name),
    farmId: d.farmId != null && d.farmId !== '' ? String(d.farmId) : '',
    roomId: d.roomId != null && d.roomId !== '' ? String(d.roomId) : '',
    controller: controllerType,
    controllerType,
    ownership: d.ownership || getDeviceOwnership(d),
    control: d.control || null,
    mqttEnabled,
    status: checkOnlineStatus({
      ...d,
      mqttEnabled,
      timestamp: deviceStatus.lastSeenAt || d.lastSeenAt,
      lastUpdated: deviceStatus.lastSeenAt || d.lastSeenAt,
      status: deviceStatus.status || d.status,
    })
      ? 'online'
      : 'offline',
    lastUpdated: telemetryTimestamp,
    timestamp: telemetryTimestamp,
    lastTelemetryAt: telemetryTimestamp,
    latestTelemetryAt: telemetryTimestamp,
    lastRealSensorUpdateAt: telemetryTimestamp,
    latestTelemetry: telemetry,
    sensorDataFresh: sensorDataFreshness.fresh === true,
    sensorDataFreshness,
    deviceStatus: {
      status: deviceStatus.status || d.status || 'offline',
      lastSeenAt: deviceStatus.lastSeenAt || d.lastSeenAt || null,
      changedAt: deviceStatus.changedAt || null,
    },
    alertsEnabled: d.alertsEnabled !== false,
    telegramEnabled: d.telegramEnabled ?? d.alertsEnabled !== false,
    powerAlerts: d.powerAlerts ?? d.notifyOnOffline ?? true,
    thresholdAlerts: d.thresholdAlerts ?? d.alertsEnabled !== false,
    trendAlerts: d.trendAlerts ?? d.trendAlertsEnabled ?? false,
    notificationDelivery: d.notificationDelivery || null,

    temperature: sensorSource.temperature,
    humidity: sensorSource.humidity,
    co2: sensorSource.co2,
    vpd: sensorSource.vpd,
    light: sensorSource.light,
    lightStatus: sensorSource.lightStatus,
    rA: relayAc ?? sensorSource.rA,
    rD: relayDryer ?? sensorSource.rD,
    rC: relayCo2 ?? sensorSource.rC,
    rL: relayExtra ?? sensorSource.rL,
    rb: sensorSource.rb,
    relays: {
      ...(d.relays || d.meta?.relays || {}),
      ...(relayAc !== undefined ? { ac: relayAc, rA: relayAc } : {}),
      ...(relayDryer !== undefined ? { dryer: relayDryer, rD: relayDryer } : {}),
      ...(relayCo2 !== undefined ? { co2: relayCo2, rC: relayCo2 } : {}),
      ...(relayExtra !== undefined ? { extra: relayExtra, rL: relayExtra } : {}),
    },
    ph: sensorSource.ph,
    ec: sensorSource.ec,
    orp: sensorSource.orp,
    temp: sensorSource.temp ?? sensorSource.temperature,
    ecTemp: sensorSource.ecTemp ?? sensorSource.temp ?? sensorSource.temperature,
    level: sensorSource.level,
    levelCm: sensorSource.levelCm ?? sensorSource.level_cm,
    volume: sensorSource.volume,
    levelVoltage: sensorSource.levelVoltage ?? sensorSource.level_voltage,
    phError: sensorSource.phError,
    ecError: sensorSource.ecError,
    orpError: sensorSource.orpError,
    tankMaxHeight: sensorSource.tankMaxHeight,
    tankFullVolume: sensorSource.tankFullVolume,
    transport: sensorSource.transport ?? d.meta?.transport,
    telemetry_reason: sensorSource.telemetry_reason ?? sensorSource.telemetryReason ?? d.meta?.telemetry_reason,
    telemetryReason: sensorSource.telemetryReason ?? sensorSource.telemetry_reason ?? d.meta?.telemetry_reason,

    dayStatus: sensorSource.dayStatus,
    preset: sensorSource.preset,
    deviceStatuses: resolvedStatuses,
    co2Warning: d.co2Warning || null,
    thresholds: normalizedThresholds,
    sockets: sensorSource.sockets || d.sockets || {},
    socketStates: sensorSource.socketStates || d.socketStates || {},
    meta: d.meta || {},

    parsedReadings: {
      co2: sensorSource.co2 != null ? `${sensorSource.co2} ppm` : null,
      temperature: sensorSource.temperature != null ? `${sensorSource.temperature} °C` : null,
      humidity: sensorSource.humidity != null ? `${sensorSource.humidity} %` : null,
      vpd: sensorSource.vpd != null ? `${sensorSource.vpd} kPa` : null,
      light: sensorSource.light,
      lightStatus: sensorSource.lightStatus,
      thresholds: normalizedThresholds,
      sockets: {
        temperature: safeField(
          sensorSource.socketStates?.temperature ??
            sensorSource.sockets?.temperature ??
            sensorSource.sockets?.temp ??
            (resolvedStatuses?.ac === 'active'
              ? 'active'
              : resolvedStatuses?.ac === 'inactive'
                ? 'inactive'
                : null)
        ),
        humidity: safeField(
          sensorSource.socketStates?.humidity ??
            sensorSource.sockets?.humidity ??
            (resolvedStatuses?.dehumidifier === 'active'
              ? 'active'
              : resolvedStatuses?.dehumidifier === 'inactive'
                ? 'inactive'
                : null)
        ),
        co2: safeField(
          sensorSource.socketStates?.co2 ??
            sensorSource.sockets?.co2 ??
            (resolvedStatuses?.co2 === 'active'
              ? 'active'
              : resolvedStatuses?.co2 === 'inactive'
                ? 'inactive'
                : null)
        ),
      },
    },
  };
}
